import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AppHeader } from '@/components/layout/AppHeader';
import { PolicyTable } from '@/components/policies/PolicyTable';
import { PolicyFilters, PolicySortOption } from '@/components/policies/PolicyFilters';
import { usePolicies, usePolicyCategories, Policy } from '@/hooks/usePolicies';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Plus, CheckCircle, AlertTriangle, ChevronRight, Search, Archive, X, User, ArrowLeft, Upload } from 'lucide-react';
import { BackArrow } from '@/components/BackArrow';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { PolicyStatusBadge } from '@/components/policies/PolicyStatusBadge';
import { useAckStats } from '@/hooks/useAckStats';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export default function Policies() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const viewParam = searchParams.get('view');
  const statusParam = searchParams.get('status');
  const globalSearchParam = searchParams.get('search');
  const composerMode = searchParams.get('composerMode') as 'update' | 'draft' | null;
  const { profile } = useAuth();
  
  const { data: policies, isLoading, error } = usePolicies();
  const categories = usePolicyCategories(policies);
  const { data: ackStatsMap } = useAckStats();
  const { role } = useMyClinicRole();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const isSoftClosed = subscriptionStatus?.status === 'soft_closed';
  
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState(globalSearchParam ?? '');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedSort, setSelectedSort] = useState<PolicySortOption>('next_review');

  // Dynamic page title based on active filters
  const pageTitle = useMemo(() => {
    if (composerMode === 'draft') return 'Continue a Draft';
    if (composerMode === 'update') return 'Update a Policy';

    const isMyPolicies = viewParam === 'mine';
    const prefix = isMyPolicies ? 'My ' : '';
    
    if (globalSearchParam) {
      return isMyPolicies ? 'My Policies - Search Results' : 'Search Results';
    }
    
    // Check URL status param first, then dropdown selection
    const activeStatus = statusParam || (selectedStatuses.length === 1 ? selectedStatuses[0] : null);
    
    switch (activeStatus) {
      case 'overdue':
      case 'review_overdue':
        return `${prefix}Over Due Policies`;
      case 'due_soon':
      case 'review_due_soon':
        return `${prefix}Due Soon Policies`;
      case 'need_ack':
      case 'review_need_ack':
        return `${prefix}Policies Needing Acknowledgment`;
      case 'pending':
      case 'pending_approval':
        return `${prefix}Pending Approval Policies`;
      default:
        return isMyPolicies ? 'My Policies' : 'Policies';
    }
  }, [viewParam, statusParam, globalSearchParam, selectedStatuses]);

  // Reset all local filters when navigating via main nav (clean URL with no filters)
  useEffect(() => {
    // Detect clean navigation: URL has no status and no search params
    // This happens when user clicks "My Policies" or "All Policies" in nav
    if (!statusParam && !globalSearchParam) {
      setSelectedStatuses([]);
      setSelectedCategories([]);
      setSearchTerm('');
      setShowArchived(false);
    }
  }, [statusParam, globalSearchParam, viewParam]);

  // Sync local search with URL param when it exists
  useEffect(() => {
    if (globalSearchParam) {
      setSearchTerm(globalSearchParam);
    }
  }, [globalSearchParam]);

  // Fetch acknowledgement status when need_ack filter is active
  const { data: ackStatusMap } = useQuery({
    queryKey: ['policy-ack-status', profile?.id, statusParam],
    queryFn: async () => {
      if (!profile?.id) return new Map<string, boolean>();
      
      // Get all policy versions
      const { data: versions } = await supabase
        .from('policy_versions')
        .select('id, policy_id')
        .order('version_number', { ascending: false });
      
      if (!versions?.length) return new Map<string, boolean>();
      
      // Get latest version per policy
      const latestVersions = new Map<string, string>();
      versions.forEach(v => {
        if (!latestVersions.has(v.policy_id)) {
          latestVersions.set(v.policy_id, v.id);
        }
      });
      
      // Get user's acknowledgements
      const versionIds = Array.from(latestVersions.values());
      const { data: acks } = await supabase
        .from('policy_acknowledgements')
        .select('policy_version_id')
        .eq('acknowledged_by', profile.id)
        .in('policy_version_id', versionIds);
      
      // Create a map of policy_id -> acknowledged
      const ackMap = new Map<string, boolean>();
      latestVersions.forEach((versionId, policyId) => {
        ackMap.set(policyId, acks?.some(a => a.policy_version_id === versionId) ?? false);
      });
      
      return ackMap;
    },
    enabled: statusParam === 'need_ack' && !!profile?.id,
  });

  // Sync dropdown with URL status param on initial load
  useEffect(() => {
    const statusMap: Record<string, string> = {
      'overdue': 'review_overdue',
      'due_soon': 'review_due_soon',
      'need_ack': 'review_need_ack',
      'pending': 'pending_approval',
    };
    
    if (statusParam && statusMap[statusParam]) {
      setSelectedStatuses([statusMap[statusParam]]);
    } else if (statusParam) {
      setSelectedStatuses([statusParam]);
    }
  }, [statusParam]);

  // Handler for dropdown status change - clears URL param and applies new filter
  function handleStatusesChange(statuses: string[]) {
    setSelectedStatuses(statuses);
    // Clear the URL status param when dropdown changes
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('status');
    setSearchParams(newParams);
  }

  const filteredPolicies = useMemo(() => {
    if (!policies) return [];
    
    const todayStr = new Date().toISOString().split('T')[0];
    const thirtyDays = new Date();
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    const thirtyDaysStr = thirtyDays.toISOString().split('T')[0];

    // Start with all policies, then filter by archive status
    let result = policies.filter((policy) => {
      if (!showArchived && policy.is_archived) return false;
      return true;
    });

    // Apply "My Policies" filter - only show policies owned by current user
    if (viewParam === 'mine' && profile?.id) {
      result = result.filter((policy) => policy.policy_owner === profile.id);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((policy) => 
        policy.name.toLowerCase().includes(term) ||
        policy.category.toLowerCase().includes(term)
      );
    }

    // Apply URL-based status filtering
    if (statusParam === 'overdue') {
      result = result.filter((policy) => {
        if (!policy.next_review_date) return false;
        return policy.next_review_date < todayStr && policy.status === 'Approved';
      });
    } else if (statusParam === 'due_soon') {
      result = result.filter((policy) => {
        if (!policy.next_review_date) return false;
        return policy.next_review_date >= todayStr && policy.next_review_date <= thirtyDaysStr && policy.status === 'Approved';
      });
    } else if (statusParam === 'need_ack') {
      // Only show policies that actually still need acknowledgement
      result = result.filter((policy) => {
        if (policy.status !== 'Approved') return false;
        if (!policy.requires_acknowledgement) return false;
        // Exclude fully acknowledged policies or policies with no ack stats (no versions)
        if (ackStatsMap) {
          const stats = ackStatsMap.get(policy.id);
          if (!stats || stats.isFullyAcknowledged) return false;
        }
        return true;
      });
    } else if (statusParam === 'pending') {
      result = result.filter((policy) => policy.status === 'pending_approval');
    }

    // Then apply dropdown filters
    let filtered = result.filter((policy) => {
      // Multi-select category filter
      if (selectedCategories.length > 0 && !selectedCategories.includes(policy.category)) return false;
      
      // Multi-select status filter
      if (selectedStatuses.length > 0) {
        const matchesAny = selectedStatuses.some((s) => {
          if (s === 'review_overdue') {
            return policy.next_review_date && policy.next_review_date < todayStr && policy.status === 'Approved';
          }
          if (s === 'review_due_soon') {
            return policy.next_review_date && policy.next_review_date >= todayStr && policy.next_review_date <= thirtyDaysStr && policy.status === 'Approved';
          }
          if (s === 'review_need_ack') {
            if (policy.status !== 'Approved') return false;
            if (!policy.requires_acknowledgement) return false;
            if (ackStatsMap) {
              const stats = ackStatsMap.get(policy.id);
              if (!stats || stats.isFullyAcknowledged) return false;
            }
            return true;
          }
          return policy.status === s;
        });
        if (!matchesAny) return false;
      }
      
      return true;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      switch (selectedSort) {
        case 'next_review':
          return new Date(a.next_review_date).getTime() - new Date(b.next_review_date).getTime();
        case 'last_approved':
          return new Date(b.last_review_date ?? '1970-01-01').getTime() - new Date(a.last_review_date ?? '1970-01-01').getTime();
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'category':
          return a.category.localeCompare(b.category) || a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    // Sort by ack status when need_ack filter is active (unacknowledged first)
    if (statusParam === 'need_ack' && ackStatusMap) {
      filtered.sort((a, b) => {
        const aAcked = ackStatusMap.get(a.id) ?? false;
        const bAcked = ackStatusMap.get(b.id) ?? false;
        if (aAcked === bAcked) return 0;
        return aAcked ? 1 : -1;
      });
    }

    return filtered;
  }, [policies, selectedCategories, selectedStatuses, statusParam, viewParam, profile?.id, ackStatusMap, ackStatsMap, searchTerm, showArchived, selectedSort]);

  function handleClearFilters() {
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSearchTerm('');
    // Clear the search param from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    setSearchParams(newParams);
  }

  function handleClearGlobalSearch() {
    setSearchTerm('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    setSearchParams(newParams);
  }

  function handleClearStatusFilter() {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('status');
    newParams.delete('search');
    setSearchTerm('');
    setSelectedStatuses([]);
    setSearchParams(newParams);
  }

  const isFirstRun = !isLoading && !error && (policies?.length ?? 0) === 0 && !composerMode && !searchTerm && !statusParam && !globalSearchParam && selectedCategories.length === 0 && selectedStatuses.length === 0;

  return (
    <div className="min-h-screen bg-background pb-14 md:pb-0">
      <AppHeader />
      
      <main className="container px-4 py-8">
        {/* Composer mode banner */}
        {composerMode && (
          <div className="mb-4 flex items-center gap-3 rounded-[8px] border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
            <span className="text-primary font-medium flex-1">
              {composerMode === 'update'
                ? 'Select a policy to update in Policy Builder.'
                : 'Select a draft to continue in Policy Builder.'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80 gap-1"
              onClick={() => navigate('/composer')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        )}

        <div className="mb-6 flex items-center justify-between gap-2">
          <div className="flex items-center min-w-0">
            <BackArrow to="/" />
            {viewParam === 'mine' ? (
              <User className="h-5 w-5 md:h-6 md:w-6 text-primary mr-2 md:mr-3 shrink-0" />
            ) : (
              <FileText className="h-5 w-5 md:h-6 md:w-6 text-primary mr-2 md:mr-3 shrink-0" />
            )}
            <h1 className="text-2xl md:text-[32px] font-extrabold tracking-tight whitespace-nowrap">{pageTitle}</h1>
            {!isLoading && !error && (
              <span className="ml-2.5 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold tabular-nums min-w-[22px] h-[22px] px-1.5 leading-none">
                {filteredPolicies.length}
              </span>
            )}
          </div>
          {role !== 'staff' && !composerMode && !isSoftClosed && (
            <div className="flex items-center gap-2 shrink-0">
              <Button asChild size="sm" variant="outline">
                <Link to="/policies/import">
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Import Existing</span>
                  <span className="sm:hidden">Import</span>
                </Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/policies/new">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Create Policy</span>
                  <span className="sm:hidden">New</span>
                </Link>
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <div className="flex gap-3">
              <Skeleton className="h-10 w-[180px]" />
              <Skeleton className="h-10 w-[150px]" />
            </div>
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            Failed to load policies. Please try again.
          </div>
        ) : isFirstRun ? (
          <PolicyEmptyState role={role} isSoftClosed={isSoftClosed} />
        ) : (
          <div className="space-y-4">
            {/* Global search indicator */}
            {globalSearchParam && (
              <div className="flex items-center gap-2 py-2 px-3 rounded-[8px] bg-primary/10 border border-primary/20 text-sm">
                <Search className="h-4 w-4 text-primary" />
                <span>
                  Searching for: <strong>"{globalSearchParam}"</strong>
                </span>
                <button
                  onClick={handleClearGlobalSearch}
                  className="ml-auto text-primary hover:text-primary/80 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            
            {!composerMode && (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search policies..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-[200px] h-10 bg-card border-border"
                    />
                  </div>
                  <PolicyFilters
                    categories={categories}
                    selectedCategories={selectedCategories}
                    selectedStatuses={selectedStatuses}
                    selectedSort={selectedSort}
                    onCategoriesChange={setSelectedCategories}
                    onStatusesChange={handleStatusesChange}
                    onSortChange={setSelectedSort}
                    onClearFilters={handleClearFilters}
                  />
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <Checkbox
                      checked={showArchived}
                      onCheckedChange={(checked) => setShowArchived(checked === true)}
                    />
                    Include archived
                  </label>
                </div>
              </div>
            )}
            
            {statusParam === 'need_ack' ? (
              <NeedAckTable policies={filteredPolicies} ackStatusMap={ackStatusMap} showArchived={showArchived} />
            ) : (
              <PolicyTable policies={filteredPolicies} ackStatusMap={ackStatusMap} composerMode={composerMode ?? undefined} />
            )}

          </div>
        )}
      </main>
    </div>
  );
}

function PolicyEmptyState({ role, isSoftClosed }: { role: string | null; isSoftClosed: boolean }) {
  if (role === 'staff' || isSoftClosed) {
    return (
      <div className="rounded-[8px] border border-border bg-card p-12 text-center">
        <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">No policies yet</h2>
        <p className="text-muted-foreground text-sm max-w-sm mx-auto">
          Your clinic is still setting things up. Check back soon — policies you need to acknowledge will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[8px] border border-border bg-card p-8 md:p-12">
      <div className="text-center mb-8">
        <FileText className="h-10 w-10 text-primary mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Let's get your policies on record.</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          Veracord is your system of record. Add your first policy to start building your audit-ready compliance library.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        <Link
          to="/policies/import"
          className="group flex flex-col gap-3 rounded-[8px] border border-border bg-background p-6 hover:border-primary/50 hover:bg-primary/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
              <Upload className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Already have policies?</p>
              <p className="text-xs text-muted-foreground">Import existing documents</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Bring in your approved PDFs or Word docs. Veracord records them as approved with a full audit trail — no re-approval needed.
          </p>
          <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary">
            Import Existing <ChevronRight className="h-4 w-4" />
          </span>
        </Link>

        <Link
          to="/policies/new"
          className="group flex flex-col gap-3 rounded-[8px] border border-border bg-background p-6 hover:border-primary/50 hover:bg-primary/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
              <Plus className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Starting from scratch?</p>
              <p className="text-xs text-muted-foreground">Create a new policy</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Write, submit for approval, and lock a policy through the full compliance workflow — tracked from day one.
          </p>
          <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary">
            Create Policy <ChevronRight className="h-4 w-4" />
          </span>
        </Link>
      </div>
    </div>
  );
}

// Inline table component for need_ack view with Ack Status column
function NeedAckTable({ 
  policies, 
  ackStatusMap,
  showArchived
}: { 
  policies: Policy[]; 
  ackStatusMap: Map<string, boolean> | undefined;
  showArchived: boolean;
}) {
  const { data: ackStatsMap } = useAckStats();

  if (policies.length === 0) {
    return (
      <div className="rounded-[8px] border border-border bg-card p-8 text-center text-muted-foreground">
        No policies found matching your filters.
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="rounded-[8px] border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader className="bg-[hsl(var(--table-header))]">
            <TableRow>
              <TableHead className="w-[280px]">Policy Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ack Status</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Next Review</TableHead>
              <TableHead className="w-[40px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {policies.map((policy) => {
              const stats = ackStatsMap?.get(policy.id);
              const isFullyAcked = stats?.isFullyAcknowledged ?? false;

              return (
                <TableRow 
                  key={policy.id} 
                  className={cn(
                    "group",
                    policy.is_archived && "bg-muted/50"
                  )}
                >
                  <TableCell className="font-medium">
                    <Link 
                      to={policy.status === 'draft' ? `/composer?policy_id=${policy.id}` : `/policies/${policy.id}`}
                      className="hover:underline"
                    >
                      {policy.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {policy.category}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <PolicyStatusBadge status={policy.status} />
                      {policy.is_archived && (
                        <Badge
                          variant="outline"
                          className="gap-1 text-muted-foreground border-muted-foreground/30"
                        >
                          <Archive className="h-3 w-3" />
                          Archived
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <AckStatusBadge policyId={policy.id} stats={stats} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {policy.owner_profile?.full_name ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(policy.next_review_date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Link to={policy.status === 'draft' ? `/composer?policy_id=${policy.id}` : `/policies/${policy.id}`}>
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}

// Reusable Ack Status badge with tooltip and link
function AckStatusBadge({ policyId, stats }: { policyId: string; stats?: { totalStaff: number; pendingCount: number; pendingNames: string[]; isFullyAcknowledged: boolean } }) {
  if (!stats) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  if (stats.isFullyAcknowledged) {
    return (
      <Badge
        variant="outline"
        className="gap-1.5 font-medium border-status-approved/30 bg-status-approved/10 text-status-approved"
      >
        <CheckCircle className="h-3 w-3" />
        Acknowledged
      </Badge>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to={`/policies/${policyId}?tab=acknowledgements`}>
          <Badge
            variant="outline"
            className="gap-1.5 font-medium border-primary/30 bg-primary/10 text-primary cursor-pointer hover:bg-primary/20 transition-colors"
          >
            <AlertTriangle className="h-3 w-3" />
            Needs Ack ({stats.pendingCount}/{stats.totalStaff})
          </Badge>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[250px]">
        <p className="font-medium text-xs">Pending: {stats.pendingNames.join(', ')}</p>
      </TooltipContent>
    </Tooltip>
  );
}
